import ErrorSummary from '@/components/ErrorSummary'
import type { PoleasPublicBasicContacts, PoleasPublicSignInOptions, PoleasPublicWizardLoginInfo } from '@/dtos'
import { usePublicSignIn } from '@/hooks/usePoleasPublic'
import { InfoOutlineIcon } from '@chakra-ui/icons'
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  Text,
  VStack
} from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { useState } from 'react'
import * as yup from 'yup'


export const schema = yup.object({
  rc: yup
    .string()
    .when('$isRcLoginType', {
      is: true,
      then: (s) =>
        s.required('Pro ověření identity zadejte Vaše RČ.')
          .test(
            'rc-or-csp',
            'Rodné číslo musí obsahovat pouze číslice, nebo formát RRMMDDCSP.',
            (value) => {
              if (!value) return false
              const trimmed = value.trim().toUpperCase()

              // Varianta 1: RČ jen číslice (9 nebo 10 znaků)
              const rcRegex = /^(\d{9,10}|\d{6}\/\d{3,4})$/

              // Varianta 2: CSP – 6 číslic + "CSP"
              const cspRegex = /^\d{6}CSP$/

              return rcRegex.test(trimmed) || cspRegex.test(trimmed)
            }
          ),
      otherwise: (s) => s.notRequired(),
    }),
  ic: yup
    .string()
    .when('$isRcLoginType', {
      is: false,
      then: (s) =>
        s.required('Pro ověření identity zadejte Vaše IČ.')
          .test(
            'ic-format',
            'IČ musí být tvořeno 8 číslicemi.',
            (value) => {
              if (!value) return false
              const trimmed = value.trim()
              const icRegex = /^\d{8}$/
              return icRegex.test(trimmed)
            }
          ),
      otherwise: (s) => s.notRequired(),
    })
})

export const SignInForm = ({
  token,
  loginInfo,
  isDisabled,
  submitCallback,
}: {
  token: string
  loginInfo: PoleasPublicWizardLoginInfo
  isDisabled: boolean
  submitCallback: (data: PoleasPublicBasicContacts) => void
}) => {

  const [submitting, setSubmitting] = useState(false)

  const { mutateAsync: signIn } = usePublicSignIn()

  const schemaWithContext = schema
  const methods = useForm<PoleasPublicSignInOptions>({
    mode: 'onChange',
    resolver: yupResolver(schemaWithContext, {
      context: {
        isRcLoginType: loginInfo?.isRcLoginType
      }
    }),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = methods

  const submitForm = handleSubmit(async (formValues) => {
    try {
      setSubmitting(true)

      signIn({
        data: formValues,
        token: token!
      })
        .then(({ data }) => {
          submitCallback(data)
        })
        .catch((err) => {
          if (!(err.response && err.response.status === 400 && err.response.data))
            return

          const data = err.response.data

          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })
        })

    } finally {
      setSubmitting(false)
    }
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>

        <VStack align="stretch" spacing={5}>
          <Heading as="h2" size="md" fontWeight="semibold">
            Přihlášení
          </Heading>

          {loginInfo.isRcLoginType &&
                        <FormControl isInvalid={!!errors.rc}>
                          <HStack spacing={2} mb={1}>
                            <CustomTooltip
                              label="Pro ověření identity zadejte Vaše RČ. Rodné číslo může obsahovat pouze číslice bez mezer nebo pomlček. 
                                        Cizí státní příslušníci (bez přiděleného RČ) zadají položku ve formátu RRMMDDCSP např. 7.1.1996 -> 960107CSP."
                              trigger="clickHover"
                              hasArrow
                              placement="top"
                              maxW={'300px'}
                            >
                              <Flex alignItems={'center'} gap={2}>
                                <Icon as={InfoOutlineIcon} boxSize="4" />
                                <FormLabel m="0">Pro ověření identity zadejte Vaše rodné číslo</FormLabel>
                              </Flex>
                            </CustomTooltip>
                          </HStack>

                          <Input
                            {...register('rc')}
                            placeholder="např. 850101/1234"
                            isDisabled={isDisabled}
                          />

                          <Text mt={2} fontSize="sm" color={'gray.500'}>
                            Formát: <code>######/###( # )</code> – lomítko je volitelné.
                          </Text>
                        </FormControl>
          }

          {!loginInfo.isRcLoginType &&
                        <FormControl isInvalid={!!errors.ic} isRequired>
                          <HStack spacing={2} mb={1}>
                            <Flex alignItems={'center'} gap={2}>
                              <FormLabel m="0">Pro ověření identity zadejte Vaše IČ.</FormLabel>
                            </Flex>
                          </HStack>

                          <Input
                            {...register('ic')}
                            isDisabled={isDisabled}
                            // bg="white"
                          />
                        </FormControl>
          }

          {!isDisabled &&
                        <Button
                          type="submit"
                          size="lg"
                          bg={'red.500'}
                          _hover={{ bg: '#c6261a' }}
                          color="white"
                          isLoading={submitting}
                          isDisabled={submitting}
                          loadingText="Ověřuji…"
                        >
                          Ověřit uživatele
                        </Button>
          }

          <ErrorSummary errors={errors} />
        </VStack>

      </form>
    </FormProvider>
  )
}
